import { useState, useEffect, useRef } from "react";
import { toCalendarDay, fromCalendarDay } from '../../utils/dateFormatter';
import { useParams, useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import PageTabs from '../shared/PageTabs';
import { buildCourseTabs } from '../shared/tabConfigs';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Button,
  Grid,
  Autocomplete,
  TextField,
  Snackbar,
  Alert, Tab, Tabs, IconButton, Card,
  CardContent,
  Stack,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { courseService } from "../../services/api";
import { SortablePrerequisite } from "./SortablePrerequisite";
import { useUser } from "../../context/UserContext";
const DropzoneArea = styled(Box)(({ theme })=> ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const CourseSettings = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const initialized = useRef(false);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const { user } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);

  
  const permissions = user?.permissions || [];

  const courseTabs = buildCourseTabs(id, { permissions: user?.permissions || [], roleId: user?.role_id });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [settings, setSettings] = useState({
    enrollment_type: "self_enroll",
    self_unenrollment: true,
    switch_instance: false,
    auto_retire: null,
    authors: [],
    prerequisites: [],
    status: 1,
    resourse_type: [],
    attachments: [],
    course_end_date: null,
    compliance_deadline_type: 'fixed',
    compliance_deadline_days: '',
    mandatory_course: false
  });
  const [authorOptions, setAuthorOptions] = useState([]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await courseService.getSettings(id);
        const { data } = response.data;
        const nameParts = user.name ? user.name.split(" ") : ["", ""];
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        const currentUser = {
          id: user.id,
          first_name: firstName,
          last_name: lastName,
        };
        const selectedAuthors = data.selectedAuthors.map((author) => ({
          id: author.id,
          first_name: author.first_name,
          last_name: author.last_name,
        }));

        const currentUserExists = selectedAuthors.some(
          (author) => author.id === currentUser.id
        );
        const finalAuthors = currentUserExists
          ? selectedAuthors
          : [currentUser, ...selectedAuthors];

        setSettings({
          enrollment_type: data.enrollment_type || "self_enroll",
          self_unenrollment: data.course_end_date ? false : (data.self_unenrollment === '0' ? false : true),
          switch_instance: data.switch_instance === true,
          // The dates come and go as calendar days (S-19 (f) sub-point). Removed 17 Oct 2026 (S-19 (f)): reading them
          // as UTC times and sending toISOString's day, so a date moved a day east or west of UTC.
          auto_retire: fromCalendarDay(data.auto_retire),
          authors: finalAuthors,
          prerequisites:
            data.prerequisites?.map((p) => ({
              id: p.cp_id,
              name: p.title,
              prerequisite_id: p.prerequisite_id,
              required: p.required === "1",
            })) || [],
          status: data.course_info.status,
          resourse_type: data.resourse_type || 'url',
          attachments: data.attachments || [],
          mandatory_course:
            data.mandatory_course === '1' ||
            data.mandatory_course === 1 ||
            data.mandatory_course === true,
          compliance_deadline_type:
            data.compliance_deadline_type || 'fixed',
          compliance_deadline_days:
            data.compliance_deadline_days || '',
          course_end_date: fromCalendarDay(data.course_end_date)
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    setSettings((prev) => {
      const oldIndex = prev.prerequisites.findIndex(p => String(p.id) === String(active.id));
      const newIndex = prev.prerequisites.findIndex(p => String(p.id) === String(over.id));
      if (oldIndex === -1 || newIndex === -1) return prev;

      const newOrder = arrayMove(prev.prerequisites, oldIndex, newIndex);

      (async () => {
        try {
          const formData = {
            prerequisites: newOrder.map(p => p.id),
            prerequisites_mandatory: newOrder
              .filter(p => p.required === '1' || p.required === 1 || p.required === true)
              .map(p => p.id),
          };
          await courseService.updatePrerequisitesOrdering(formData);
        } catch (err) {
          console.error("Failed to update prerequisite order:", err);
        }
      })();

      return { ...prev, prerequisites: newOrder };
    });
  };


  const handleRemovePrerequisite = (id) => {
    setSettings((prev) => ({
      ...prev,
      prerequisites: prev.prerequisites.filter((p) => p.id !== id),
    }));
  };

  const [errors, setErrors] = useState({});
  
  const handleSubmit = async (submitAction) => {
    const newErrors = {};

    // VALIDATE DATES BEFORE ANYTHING ELSE
    if (settings.course_end_date && settings.auto_retire) {
      if (settings.course_end_date > settings.auto_retire) {
        newErrors.course_end_date = "End Date must be less than or equal to Auto Retire Date";
        setErrors(newErrors);
        setSnackbar({
          open: true,
          message: "End Date must be less than or equal to Auto Retire Date",
          severity: "error",
        });
        return; // STOP SUBMISSION
      }
    }

    if (settings.authors.length === 0) {
      newErrors.authors = "At least one author is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      // Basic fields
      formData.append("enrollment_type", settings.enrollment_type);
      // Ensure backend always receives correct self_unenrollment value:
      const selfUnenrollValue = settings.course_end_date ? "0" : (settings.self_unenrollment ? "1" : "0");
      formData.append("self_unenrollment", selfUnenrollValue);
      formData.append("switch_instance", settings.switch_instance ? "1" : "0");
      formData.append("auto_retire", toCalendarDay(settings.auto_retire));
      formData.append("status", submitAction === "publish" ? "3" : "1");
      //Authors
      settings.authors.forEach((author) => { formData.append("authors[]", author.id); });
      // Prerequisites
      settings.prerequisites.forEach((p, i) => {
        formData.append(`prerequisites[${i}][prerequisite_id]`, p.prerequisite_id);
        formData.append(`prerequisites[${i}][required]`, p.required ? "1" : "0");
      });

      formData.append(
        "mandatory_course",
        settings.mandatory_course ? "1" : "0"
      );

      formData.append(
        "compliance_deadline_type",
        settings.mandatory_course
          ? settings.compliance_deadline_type
          : "fixed"
      );

      formData.append(
        "compliance_deadline_days",
        settings.mandatory_course &&
          settings.compliance_deadline_type === "days_from_enrolment"
          ? settings.compliance_deadline_days
          : ""
      );

      formData.append(
        "course_end_date",
        settings.mandatory_course &&
          settings.compliance_deadline_type === "fixed" &&
          settings.course_end_date
          ? toCalendarDay(settings.course_end_date)
          : ""
      );

      if (settings.files?.length > 0) {
        formData.append("resourse_type", "file");
        settings.files.forEach((file) => {
          formData.append("files[]", file);
        });
      } else if (settings.resourse_url?.trim()) {
        formData.append("resourse_type", "url"); 

        const urlObj = [{
          name: "Resource URL",
          url: settings.resourse_url.trim(),
        }];
        formData.append("urls", JSON.stringify(urlObj));
      }
      deletedAttachmentIds.forEach((id) => {
        formData.append("deleted_attachments[]", id);
      });

      await courseService.updateSettings(id, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setSnackbar({
        open: true,
        message: `Course ${submitAction === "publish" ? "published" : "saved"} successfully!`,
        severity: "success",
      });
     setTimeout(() => {
        navigate("/courses");
      }, 1000);
    } catch (error) {
      console.error(error);
      // A refused save shows the server's reason, such as which files a resource can be (S-26 pt 1.1).
      setSnackbar({
        open: true,
        message: (error.response?.status === 400 && error.response.data?.message) || "Error saving settings",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  const [authorSearch, setAuthorSearch] = useState("");
  const handleAuthorSearch = async (searchText) => {
    const response = await courseService.getAuthors(searchText);
    setAuthorOptions(response.data);
  };

  const isPastDate = (dateValue) => {
    if (!dateValue) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateValue);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  const isInvalidDateRelationship = () => {
    return !!settings.course_end_date && !!settings.auto_retire && settings.course_end_date > settings.auto_retire;
  };

  const isInvalidAutoRetire = () => {
    return !!settings.auto_retire && !!settings.course_end_date && settings.auto_retire < settings.course_end_date;
  };

  const getDateFieldSx = (isInvalid) => ({
    opacity: isInvalid ? 0.55 : 1,
    '& .MuiInputBase-input': {
      color: isInvalid ? 'text.disabled' : 'inherit',
      cursor: 'pointer',
    },
  });

  const handleClearAutoRetire = () => {
    setSettings(prev => ({ ...prev, auto_retire: null }));
  };

  const handleClearCourseEndDate = () => {
    setSettings(prev => {
      const updatedSettings = { ...prev, course_end_date: null };
      // If enrollment type is self_enroll, restore self_unenrollment to true when end date is removed
      if (prev.enrollment_type === "self_enroll") {
        updatedSettings.self_unenrollment = true;
      }
      return updatedSettings;
    });
  };

  const handlePrerequisiteSearch = async (searchText) => {
    const response = await courseService.searchPrerequisites(searchText);
    setCourses(response.data.aaData);
  };

  const handlePrerequisiteToggle = (id, required) => {
    setSettings((prev) => ({
      ...prev,
      prerequisites: prev.prerequisites.map((p) =>
        p.id === id ? { ...p, required } : p
      ),
    }));
  };

  // Function to handle author changes
  const handleAuthorsChange = (event, newValue) => {
    // Ensure the current user is always included
    const currentUserAuthor = settings.authors.find(
      (author) => author.id === user.id
    );
    setSettings((prev) => ({
      ...prev,
      authors: currentUserAuthor
        ? [
          currentUserAuthor,
          ...newValue.filter((author) => author.id !== user.id),
        ]
        : newValue,
    }));
  };

  const filterAuthorOptions = (options) => {
    return options.filter(
      (option) => !settings.authors.some((author) => author.id === option.id)
    );
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSettings(prev => ({
      ...prev,
      resourse_type: newValue === 0 ? 'url' : 'file',
      // Clear the other type's data
      ...(newValue === 0 ? { files: [] } : { resourse_url: '' })
    }));
  };
  const handleChange = (e) => {
    if (e && e.target) {
      const { name, value, type, checked } = e.target;
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    } else if (typeof e === 'object' && e !== null && ('name' in e || 'value' in e)) {
      // For custom components that pass an object directly
      const { name, value } = e;
      setSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const removeAttachment = async (index) => {
  const attachment = settings.attachments[index];

  if (attachment?.id) {
    try {
      await courseService.deleteAttachment(attachment.id);
      setSettings((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
    } catch (err) {
      console.error("Failed to delete attachment:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete attachment",
        severity: "error",
      });
    }
  } else {
    // If it’s a newly added file, just remove from state
    setSettings((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  }
};

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setSettings(prev => {
        const existingNames = prev.files?.map(f => f.name) || [];
        const newFiles = acceptedFiles.filter(f => !existingNames.includes(f.name));
        return {
          ...prev,
          files: [...(prev.files || []), ...newFiles],
        };
      });
    },
    // The files a resource can be (S-26 pt 1.1): PDF, DOCX or TXT, as the server checks. Removed 17 Oct 2026 (S-26 pt
    // 1.1): DOC, PPT, PPTX, XLS, XLSX and ZIP, which the server refuses.
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    disabled: tabValue !== 1
  });


  return (
    <Box sx={{p: 2}}>
      <PageTabs tabs={courseTabs} />
      <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
        <Typography variant="h5" sx={{ mb: 4 }}>
          Course Settings
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* <FormControl fullWidth variant="outlined">
                  <InputLabel shrink id="enrollment-type-label">
                    Enrollment Type
                  </InputLabel>
                  <Select
                    labelId="enrollment-type-label"
                    value={settings.enrollment_type}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        enrollment_type: e.target.value,
                      }))
                    }
                    padding={'0 !important'}
                    label="Enrollment Type"                  
                  > */}
                    {/* <MenuItem value="self_enroll">Self Enroll</MenuItem> */}
                    {/* <MenuItem value="manager_approved">Manager Approved</MenuItem> */}
                    {/* <MenuItem value="manager_nominated">Manager Nominated</MenuItem> */}
                  {/* </Select>
                </FormControl> */}
              </Grid>

              {/* <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.switch_instance}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          switch_instance: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Allow Instance Switching"
                />
              </Grid> */}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <DatePicker
                label="Auto Retire Date"
                value={settings.auto_retire}
                onChange={(newValue) => {
                  setSettings((prev) => ({
                    ...prev,
                    auto_retire: newValue,
                  }))
                }}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    inputProps: { readOnly: true },
                    sx: getDateFieldSx(isInvalidAutoRetire()),
                  },
                }}
              />
              <IconButton
                aria-label="clear auto retire"
                onClick={handleClearAutoRetire}
                size="large"
                sx={{
                  bgcolor: 'transparent',
                  '&:hover': { backgroundColor: 'transparent' },
                  p: 0,
                  alignSelf: 'center',
                  minWidth: 36,
                  height: 36,
                  borderRadius: '6px',
                  '& .MuiSvgIcon-root': {
                    fontWeight: 700,
                    fontSize: '20px',
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Box>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Auto-retire date must be after the compliance date.
              </Typography>
            </Grid>          
          </Grid>
          

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.mandatory_course}
                  onChange={(event) =>
                    setSettings((previous) => ({
                      ...previous,
                      mandatory_course: event.target.checked
                    }))
                  }
                />
              }
              label="Required Course"
            />
          </Grid>
          {settings.mandatory_course && (
          <>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={!settings.mandatory_course}>
                <InputLabel>Compliance deadline type</InputLabel>
                <Select
                  value={settings.compliance_deadline_type}
                  label="Compliance deadline type"
                  onChange={(event) =>
                    setSettings((previous) => ({
                      ...previous,
                      compliance_deadline_type: event.target.value,
                      course_end_date:
                        event.target.value === 'fixed'
                          ? previous.course_end_date
                          : null
                    }))
                  }
                >
                  <MenuItem value="fixed">Fixed date</MenuItem>
                  <MenuItem value="days_from_enrolment">
                    {/* American English on every screen, here and in the field below (K2.3 pt 17) */}
                    Days from enrollment
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          
            {settings.compliance_deadline_type === 'days_from_enrolment' && (
              <Grid item xs={12}>
                <FormControl>
                    <TextField
                      fullWidth
                      type="number"
                      label="Days from enrollment"
                      value={settings.compliance_deadline_days}
                      disabled={!settings.mandatory_course}
                      onChange={(event) =>
                        setSettings((previous) => ({
                          ...previous,
                          compliance_deadline_days: event.target.value
                        }))
                      }
                    />
                </FormControl> 
              </Grid>
            )}                  
            {settings.compliance_deadline_type === 'fixed' && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <DatePicker
                    label="Compliance (End Date)"
                    value={settings.course_end_date}
                    onChange={(newValue) =>  {
                      setSettings((prev) => {
                        const updatedSettings = {
                          ...prev,
                          course_end_date: newValue,
                        };

                        // Condition 1: If End Date is set, turn off self_unenrollment
                        if (newValue) {
                          updatedSettings.self_unenrollment = false;
                          //updatedSettings.mandatory_course = false; 
                        }
                        // If End Date is removed and enrollment is self_enroll, turn on self_unenrollment
                        else if (prev.enrollment_type === "self_enroll") {
                          updatedSettings.self_unenrollment = true;
                        }

                        return updatedSettings;
                      });
                    }}
                    disablePast
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        inputProps: { readOnly: true },
                        sx: getDateFieldSx(isInvalidDateRelationship()),
                      },
                    }}
                    maxDate={settings.auto_retire || null}
                    disabled={
                      !settings.mandatory_course ||
                      settings.compliance_deadline_type !== 'fixed'
                    }
                  />
                  <IconButton
                    aria-label="clear end date"
                    onClick={handleClearCourseEndDate}
                    size="large"
                    sx={{
                      bgcolor: 'transparent',
                      '&:hover': { backgroundColor: 'transparent' },
                      p: 0,
                      alignSelf: 'center',
                      minWidth: 36,
                      height: 36,
                      borderRadius: '6px',
                      '& .MuiSvgIcon-root': {
                        fontWeight: 700,
                        fontSize: '20px',
                      },
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Box>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {settings.course_end_date
                      ? 'Self Enrollment and Mandatory Course options are disabled when Compliance is enabled.'
                      : 'Learners must complete the course by this date.'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </>
          )}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Course Authors
            </Typography>
            <Autocomplete
              multiple
              options={filterAuthorOptions(authorOptions)}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name}`
              }
              value={settings.authors || []}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(event, value) => {
                setAuthorSearch(value);
                handleAuthorSearch(value);
              }}
              onChange={handleAuthorsChange}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {`${option.first_name} ${option.last_name}`}
                </li>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                  const { onDelete, ...tagProps } = getTagProps({ index });
                  return (
                    <div
                      {...tagProps}
                      key={option.id}
                      style={{
                        margin: "3px",
                        backgroundColor: "#f5f5f5",
                        border: "1px solid #2563eb",
                        color: "#666",
                        borderRadius: "8px",
                        padding: "4px 8px",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {`${option.first_name} ${option.last_name}`}
                      {option.id !== user.id && (
                        <span
                          onClick={() => {
                            // Remove this author if it's not the current user
                            setSettings((prev) => ({
                              ...prev,
                              authors: prev.authors.filter(
                                (a) => a.id !== option.id
                              ),
                            }));
                          }}
                          style={{
                            marginLeft: "4px",
                            cursor: "pointer",
                            color: "#2563eb",
                          }}
                        >
                          ✕
                        </span>
                      )}
                    </div>
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Authors"
                  error={!!errors.authors}
                  helperText={errors.authors}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                    },
                  }}
                />
              )}
              ChipProps={{ style: { display: "none" } }}
              sx={{
                "& .MuiChip-root": {
                  // Override default chip styling
                  display: "none",
                },
              }}
            />
          </Grid>
          {/* Hede Perequesite for temprary
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Prerequisites
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                options={courses}
                getOptionLabel={(option) => option[1]}
                value={null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSettings((prev) => {
                      const alreadyExists = prev.prerequisites.some(
                        (p) => String(p.id) === String(newValue[0])
                      );
                      if (alreadyExists) return prev; // Prevent duplicate

                      return {
                        ...prev,
                        prerequisites: [
                          ...prev.prerequisites,
                          {
                            id: newValue[0],
                            name: newValue[1],
                            prerequisite_id: newValue[0],
                            required: true,
                          },
                        ],
                      };
                    });
                  }
                }}

                onInputChange={(event, value) => {
                  handlePrerequisiteSearch(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Prerequisites"
                    value=""
                  />
                )}
              />
            </Box>
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={settings.prerequisites.map((p) => String(p.id))}
                strategy={verticalListSortingStrategy}
              >
                {settings.prerequisites.map((prerequisite) => (
                  <SortablePrerequisite
                    key={prerequisite.id}
                    id={String(prerequisite.id)}
                    prerequisite={prerequisite}
                    onToggle={handlePrerequisiteToggle}
                    onRemove={handleRemovePrerequisite}
                  />
                ))}
              </SortableContext>

            </DndContext>
          </Grid>
          */}
          
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 0 }}>
              Resources
            </Typography>
            {Array.isArray(settings.attachments) && settings.attachments.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Resourses
                </Typography>
                <Stack spacing={1}>
                  {settings.attachments.map((attachment, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <CardContent sx={{ flexGrow: 1, py: 1 }}>
                        <Typography variant="body2">
                          {attachment.name || attachment.filename || "Attachment"}
                        </Typography>
                      </CardContent>
                      <IconButton
                        color="error"
                        onClick={() => removeAttachment(index)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}
            {/* New Attachments */}
            {(settings.files?.length > 0 || settings.resourse_url?.trim()) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  New Resourses
                </Typography>
                <Stack spacing={1}>
                  {settings.files?.map((file, index) => (
                    <Card
                      key={index}
                      variant="outlined"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <CardContent sx={{ flexGrow: 1, py: 1 }}>
                        <Typography variant="body2">{file.name}</Typography>
                      </CardContent>
                      <IconButton
                        color="error"
                        onClick={() =>
                          setSettings(prev => ({
                            ...prev,
                            files: prev.files.filter((_, i) => i !== index)
                          }))
                        }
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Card>
                  ))}

                  {/* Show the resource URL if it's selected */}
                  {tabValue === 0 && settings.resourse_url?.trim() && (
                    <Card variant="outlined" sx={{ display: "flex", alignItems: "center" }}>
                      <CardContent sx={{ flexGrow: 1, py: 1 }}>
                        <Typography variant="body2">{settings.resourse_url}</Typography>
                      </CardContent>
                      <IconButton
                        color="error"
                        onClick={() =>
                          setSettings(prev => ({ ...prev, resourse_url: '' }))
                        }
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Card>
                  )}
                </Stack>
              </Box>
            )}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="content type tabs"
              sx={{ mb: 3 }}
            >
              <Tab icon={<LinkIcon />} label="URL" />
              <Tab icon={<UploadIcon />} label="File Upload" />
            </Tabs>

            {tabValue === 0 ? (
              <TextField
                fullWidth
                label="Resourse URL"
                name="resourse_url"
                value={settings.resourse_url}
                onChange={handleChange}
                variant="outlined"
                placeholder="https://example.com/content"
                error={!!errors.resourse_url}
                helperText={errors.resourse_url}
              />
            ) : (
              <DropzoneArea {...getRootProps()}>
                <input {...getInputProps()} />
                <UploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {isDragActive ? "Drop files here" : "Drag and drop files here"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to browse
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  Supported formats: PDF, DOCX or TXT, up to 50 MB
                </Typography>
              </DropzoneArea>
            )}
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleSubmit("save")}
            disabled={loading}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSubmit("publish")}
            disabled={loading}
          >
            {settings.status === 3 ? "Republish" : "Publish"}
          </Button>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CourseSettings;
